import type { AppUpdates, UpdateCheck } from '@ims/app'

// Asking for a new version now (docs/plans/app-update.md), instead of waiting for the browser's
// own check. Never says "latest" unless the server really answered.

/** How long a new version may take to download before the check gives up. */
const INSTALL_MS = 30_000

interface WorkerLike {
  state: string
  addEventListener(type: 'statechange', fn: () => void): void
  removeEventListener(type: 'statechange', fn: () => void): void
}

export interface RegistrationLike {
  waiting: unknown
  installing: WorkerLike | null
  update(): Promise<unknown>
}

function installed(worker: WorkerLike, ms: number): Promise<UpdateCheck> {
  return new Promise((resolve) => {
    const done = (result: UpdateCheck) => {
      clearTimeout(timer)
      worker.removeEventListener('statechange', changed)
      resolve(result)
    }
    const changed = () => {
      if (worker.state === 'installed' || worker.state === 'activated') done('ready')
      else if (worker.state === 'redundant') done('unreachable')
    }
    const timer = setTimeout(() => done('unreachable'), ms)
    worker.addEventListener('statechange', changed)
    changed()
  })
}

export async function checkRegistration(
  registration: RegistrationLike | undefined,
  online: boolean,
  ms = INSTALL_MS,
): Promise<UpdateCheck> {
  if (!registration) return 'unsupported'
  if (!online) return 'offline'
  if (registration.waiting) return 'ready'
  try {
    await registration.update()
  } catch {
    // The server, or an expired staging login in front of it, did not answer.
    return 'unreachable'
  }
  if (registration.waiting) return 'ready'
  return registration.installing ? installed(registration.installing, ms) : 'latest'
}

export const browserUpdates: AppUpdates = {
  get version() {
    return __APP_VERSION__
  },
  async check() {
    if (!('serviceWorker' in navigator)) return 'unsupported'
    return checkRegistration(await navigator.serviceWorker.getRegistration(), navigator.onLine)
  },
  apply() {
    void navigator.serviceWorker.getRegistration().then((registration) => {
      navigator.serviceWorker.addEventListener('controllerchange', () => location.reload(), {
        once: true,
      })
      registration?.waiting?.postMessage({ type: 'SKIP_WAITING' })
    })
  },
}
